import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { fmtDate, creditColor, creditLabel, txnTypeLabel } from "@/lib/credits";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { EmailAttachments } from "@/components/EmailAttachments";
import {
  ArrowLeft, Loader2, SlidersHorizontal, Check, Ban, KeyRound, Mail, Send, StickyNote,
} from "lucide-react";

function StatusBadge({ status }) {
  const map = {
    active: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    pending_approval: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    suspended: "bg-[#E30613]/10 text-[#FF6069] border-[#E30613]/20",
  };
  const label = { active: "Active", pending_approval: "Pending", suspended: "Suspended" }[status] || status;
  return <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${map[status] || "bg-white/5 text-neutral-400 border-white/10"}`} data-testid="installer-status-badge">{label}</span>;
}

function Metric({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-[#141414] p-5">
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</p>
      <p className={`mt-2 font-display text-3xl font-semibold ${accent ? "text-[#FF1C2A]" : "text-white"}`}>{value}</p>
    </div>
  );
}

const TAB_TRIGGER = "rounded-lg px-4 py-2 text-sm font-medium text-neutral-400 data-[state=active]:bg-[#E30613] data-[state=active]:text-white transition-colors";
const CARD = "rounded-2xl border border-white/[0.06] bg-[#141414] p-6";
const TH = "border-b border-white/[0.06] text-xs uppercase tracking-wider text-neutral-500";

const EMAIL_TEMPLATES = {
  general: { subject: "", body: "" },
  low_credits: {
    subject: "Your SGBINOXE certificate credits are running low",
    body: "Hi,\n\nThis is a friendly reminder that your certificate credits are running low. To keep issuing installation certificates without interruption, please activate a new bottle code or reach out to us for a top-up.\n\nThank you for being a SGBINOXE partner.",
  },
  welcome: {
    subject: "Welcome to SGBINOXE Care",
    body: "Hi,\n\nWelcome aboard! Your installer account is now active. You can start logging installations, activating bottle codes, and issuing premium certificates right away.\n\nIf you have any questions, just reply to this email.",
  },
  follow_up: {
    subject: "A quick check-in from SGBINOXE",
    body: "Hi,\n\nWe wanted to check in and see how things are going with your SGBINOXE installations. If there's anything we can help with, we're here for you.\n\nBest regards,\nThe SGBINOXE Team",
  },
};

export default function AdminInstallerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [certs, setCerts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState("");

  const [creditOpen, setCreditOpen] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(null);
  const [form, setForm] = useState({ adjustment_type: "add", amount: "", reason: "", internal_notes: "" });
  const [email, setEmail] = useState({ template: "general", subject: "", body: "" });
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const sendLock = useRef(false);
  const idemKey = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/installers/${id}`);
      setData(res.data);
      const [c, a, n] = await Promise.all([
        api.get(`/installations`, { params: { installer_id: id } }).catch(() => ({ data: [] })),
        api.get(`/admin/activity`, { params: { installer_id: id, limit: 100 } }).catch(() => ({ data: [] })),
        api.get(`/admin/installers/${id}/notes`).catch(() => ({ data: [] })),
      ]);
      setCerts(c.data);
      setActivity(a.data);
      setNotes(n.data);
    } catch (e) {
      toast.error("Installer not found");
      navigate("/installers");
    } finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const amountNum = parseInt(form.amount, 10);
  const validAmount = Number.isInteger(amountNum) && amountNum >= 1;
  const newBalance = validAmount && data
    ? (form.adjustment_type === "add" ? data.available_credits + amountNum : data.available_credits - amountNum)
    : null;

  const submitCredits = async () => {
    if (!validAmount) { toast.error("Enter a whole number of at least 1"); return; }
    if (!form.reason.trim()) { toast.error("Reason is required"); return; }
    if (form.adjustment_type === "remove" && newBalance < 0) { toast.error("Cannot remove more than available"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/installers/${id}/adjust-credits`, {
        adjustment_type: form.adjustment_type, amount: amountNum, reason: form.reason, internal_notes: form.internal_notes,
      });
      toast.success("Credits adjusted");
      setCreditOpen(false);
      setForm({ adjustment_type: "add", amount: "", reason: "", internal_notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Adjustment failed");
    } finally { setBusy(false); }
  };

  const doAction = async (action) => {
    setActionBusy(action);
    try {
      await api.post(`/installers/${id}/${action}`);
      toast.success(action === "approve" ? "Installer approved" : "Installer suspended");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setActionBusy(null); }
  };

  const resetPassword = async () => {
    setActionBusy("reset");
    try {
      const { data: r } = await api.post(`/admin/installers/${id}/reset-password`);
      toast.success(r.message || "Reset link sent");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send reset"); }
    finally { setActionBusy(null); }
  };

  const applyTemplate = (t) => {
    const tpl = EMAIL_TEMPLATES[t] || EMAIL_TEMPLATES.general;
    setEmail({ template: t, subject: tpl.subject, body: tpl.body });
  };

  const sendEmail = async () => {
    if (sendLock.current) return;
    if (!email.subject.trim() || !email.body.trim()) { toast.error("Subject and message are required"); return; }
    sendLock.current = true;
    setSending(true);
    if (!idemKey.current) idemKey.current = (crypto.randomUUID && crypto.randomUUID()) || `${Date.now()}-${Math.random()}`;
    const fd = new FormData();
    fd.append("subject", email.subject);
    fd.append("body", email.body);
    fd.append("template", email.template);
    fd.append("idempotency_key", idemKey.current);
    attachments.forEach((f) => fd.append("files", f, f.name));
    try {
      const { data: r } = await api.post(`/admin/installers/${id}/email`, fd, { timeout: 120000 });
      toast.success(`${r.message || "Email sent"}${r.attachments ? ` with ${r.attachments} attachment(s)` : ""}`);
      idemKey.current = null;
      setEmailOpen(false);
      setEmail({ template: "general", subject: "", body: "" });
      setAttachments([]);
      load();
    } catch (e) {
      if (e.response?.status !== 409) idemKey.current = null;
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send email");
    } finally { sendLock.current = false; setSending(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setBusy(true);
    try {
      const { data: n } = await api.post(`/admin/installers/${id}/notes`, { note: noteText.trim() });
      setNotes((prev) => [n, ...prev]);
      setNoteText("");
      toast.success("Note added");
    } catch (e) { toast.error("Failed to add note"); }
    finally { setBusy(false); }
  };

  if (loading || !data) {
    return <DashboardLayout title="Installer"><div className="flex items-center justify-center py-24 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div></DashboardLayout>;
  }

  const inst = data.installer;

  return (
    <DashboardLayout title="Installer Details">
      <button onClick={() => navigate("/installers")} className="mb-5 inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-white" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> Back to installers</button>

      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="font-display text-2xl font-semibold tracking-tight text-white" data-testid="installer-business">{inst.business_name}</h2>
            <StatusBadge status={inst.status} />
          </div>
          <p className="mt-1 text-sm text-neutral-500">{inst.contact_name} · {inst.email} · {inst.phone}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {inst.status !== "active" && (
            <button onClick={() => doAction("approve")} disabled={actionBusy === "approve"} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="approve-btn">{actionBusy === "approve" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve</button>
          )}
          {inst.status === "active" && (
            <button onClick={() => doAction("suspend")} disabled={actionBusy === "suspend"} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05] disabled:opacity-60" data-testid="suspend-btn">{actionBusy === "suspend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Suspend</button>
          )}
          <button onClick={() => setCreditOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="adjust-credits-btn"><SlidersHorizontal className="h-4 w-4" /> Adjust Credits</button>
          <button onClick={() => setEmailOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05]" data-testid="email-installer-btn"><Mail className="h-4 w-4" /> Email</button>
          <button onClick={resetPassword} disabled={actionBusy === "reset"} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm font-medium text-neutral-200 hover:bg-white/[0.05] disabled:opacity-60" data-testid="reset-password-btn">{actionBusy === "reset" ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} Reset Password</button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-6 flex h-auto flex-wrap justify-start gap-2 bg-transparent p-0" data-testid="installer-tabs">
          <TabsTrigger value="overview" className={TAB_TRIGGER} data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="certificates" className={TAB_TRIGGER} data-testid="tab-certificates">Certificates</TabsTrigger>
          <TabsTrigger value="activations" className={TAB_TRIGGER} data-testid="tab-activations">Bottle Activations</TabsTrigger>
          <TabsTrigger value="credits" className={TAB_TRIGGER} data-testid="tab-credits">Credit History</TabsTrigger>
          <TabsTrigger value="activity" className={TAB_TRIGGER} data-testid="tab-activity">Activity Log</TabsTrigger>
          <TabsTrigger value="notes" className={TAB_TRIGGER} data-testid="tab-notes">Notes</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" data-testid="tabpanel-overview">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            <Metric label="Available Credits" value={data.available_credits} accent />
            <Metric label="Total Credits Added" value={data.total_credits_added} />
            <Metric label="Credits Used" value={data.certificates_used} />
            <Metric label="Bottles Activated" value={data.bottles_activated} />
            <Metric label="Certificates" value={data.certificates_generated} />
            <Metric label="Voided" value={data.voided_certificates} />
          </div>
          <div className={`mt-6 ${CARD}`}>
            <h3 className="mb-4 font-display text-base font-medium text-white">Account Details</h3>
            <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
              <Detail label="Business" value={inst.business_name} />
              <Detail label="Contact Name" value={inst.contact_name} />
              <Detail label="Email" value={inst.email} />
              <Detail label="Phone" value={inst.phone || "—"} />
              <Detail label="Address" value={inst.address || "—"} />
              <Detail label="Joined" value={fmtDate(inst.created_at)} />
            </div>
          </div>
        </TabsContent>

        <TabsContent value="certificates" data-testid="tabpanel-certificates">
          <div className={CARD}>
            {certs.length === 0 ? <p className="py-10 text-center text-sm text-neutral-500">No certificates issued.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead><tr className={TH}><th className="py-3 pr-3">Certificate</th><th className="py-3 pr-3">Customer</th><th className="py-3 pr-3">Vehicle</th><th className="py-3 pr-3">Date</th><th className="py-3">Status</th></tr></thead>
                  <tbody>
                    {certs.map((c) => (
                      <tr key={c.id} onClick={() => navigate(`/certificates/${c.id}`)} className="cursor-pointer border-b border-white/[0.04] hover:bg-white/[0.03]" data-testid={`cert-row-${c.id}`}>
                        <td className="py-3 pr-3 font-mono text-white">{c.certificate_number}</td>
                        <td className="py-3 pr-3 text-neutral-300">{c.customer_name}</td>
                        <td className="py-3 pr-3 text-neutral-400">{c.vehicle_year} {c.vehicle_make} {c.vehicle_model}</td>
                        <td className="py-3 pr-3 text-neutral-400">{fmtDate(c.installation_date)}</td>
                        <td className="py-3 capitalize text-neutral-300">{c.certificate_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activations" data-testid="tabpanel-activations">
          <div className={CARD}>
            {data.activation_history.length === 0 ? <p className="py-10 text-center text-sm text-neutral-500">No activations.</p> : (
              <table className="w-full text-left text-sm">
                <thead><tr className={TH}><th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Code</th><th className="py-3 pr-3">Credits</th><th className="py-3">Status</th></tr></thead>
                <tbody>
                  {data.activation_history.map((a) => (
                    <tr key={a.id} className="border-b border-white/[0.04]">
                      <td className="py-3 pr-3 text-neutral-400">{fmtDate(a.activation_date)}</td>
                      <td className="py-3 pr-3 font-mono text-white">{a.code}</td>
                      <td className="py-3 pr-3 font-semibold text-emerald-400">+{a.credits_added}</td>
                      <td className="py-3 capitalize text-neutral-300">{a.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="credits" data-testid="tabpanel-credits">
          <div className={CARD}>
            {data.credit_history.length === 0 ? <p className="py-10 text-center text-sm text-neutral-500">No credit activity.</p> : (
              <table className="w-full text-left text-sm">
                <thead><tr className={TH}><th className="py-3 pr-3">Date</th><th className="py-3 pr-3">Activity</th><th className="py-3 pr-3">Credits</th><th className="py-3">Balance</th></tr></thead>
                <tbody>
                  {data.credit_history.map((t) => (
                    <tr key={t.id} className="border-b border-white/[0.04]">
                      <td className="py-3 pr-3 text-neutral-400">{fmtDate(t.created_at)}</td>
                      <td className="py-3 pr-3 text-neutral-200">{txnTypeLabel[t.transaction_type] || t.transaction_type}</td>
                      <td className={`py-3 pr-3 font-semibold ${creditColor(t.credit_amount)}`}>{creditLabel(t.credit_amount)}</td>
                      <td className="py-3 text-neutral-300">{t.balance_after}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        <TabsContent value="activity" data-testid="tabpanel-activity">
          <div className={CARD}>
            {activity.length === 0 ? <p className="py-10 text-center text-sm text-neutral-500">No activity recorded.</p> : (
              <div className="divide-y divide-white/[0.05]">
                {activity.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-4 py-3" data-testid="activity-row">
                    <div>
                      <p className="text-sm text-white">{a.description}</p>
                      {a.reason && <p className="mt-0.5 text-xs text-neutral-500">Reason: {a.reason}</p>}
                      {a.actor_name && <p className="text-xs text-neutral-600">by {a.actor_name}</p>}
                    </div>
                    <p className="whitespace-nowrap text-xs text-neutral-500">{fmtDate(a.created_at, true)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="notes" data-testid="tabpanel-notes">
          <div className={CARD}>
            <div className="mb-5 flex items-start gap-3">
              <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} rows={2} placeholder="Add an internal note about this installer…" className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="note-input" />
              <button onClick={addNote} disabled={busy || !noteText.trim()} className="inline-flex shrink-0 items-center gap-2 self-start rounded-lg bg-[#E30613] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="add-note-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <StickyNote className="h-4 w-4" />} Add</button>
            </div>
            {notes.length === 0 ? <p className="py-8 text-center text-sm text-neutral-500">No notes yet.</p> : (
              <div className="space-y-3">
                {notes.map((n) => (
                  <div key={n.id} className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4" data-testid="note-item">
                    <p className="text-sm text-neutral-200">{n.note}</p>
                    <p className="mt-2 text-xs text-neutral-600">{n.admin_name} · {fmtDate(n.created_at, true)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Adjust credits modal */}
      <Dialog open={creditOpen} onOpenChange={setCreditOpen}>
        <DialogContent className="border-white/[0.08] bg-[#141414] text-white" data-testid="adjust-credits-modal">
          <DialogHeader><DialogTitle>Adjust Certificate Credits</DialogTitle><DialogDescription className="text-neutral-500">Add or remove certificate credits for this installer.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Adjustment Type</Label>
              <Select value={form.adjustment_type} onValueChange={(v) => setForm((f) => ({ ...f, adjustment_type: v }))}>
                <SelectTrigger className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="adjust-type"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  <SelectItem value="add">Add Credits</SelectItem>
                  <SelectItem value="remove">Remove Credits</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Number of Credits</Label>
              <Input type="number" min="1" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="adjust-amount" />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Reason</Label>
              <Input value={form.reason} onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="adjust-reason" />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Internal Notes (optional)</Label>
              <Textarea value={form.internal_notes} onChange={(e) => setForm((f) => ({ ...f, internal_notes: e.target.value }))} rows={2} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="adjust-notes" />
            </div>
            {validAmount && (
              <div className="rounded-xl border border-white/[0.06] bg-[#0A0A0A] p-4 text-sm">
                <div className="flex justify-between text-neutral-400"><span>Current Balance</span><span className="text-white">{data.available_credits}</span></div>
                <div className="flex justify-between text-neutral-400"><span>Adjustment</span><span className={creditColor(form.adjustment_type === "add" ? amountNum : -amountNum)}>{creditLabel(form.adjustment_type === "add" ? amountNum : -amountNum)}</span></div>
                <div className="mt-1 flex justify-between border-t border-white/[0.06] pt-2 font-medium"><span className="text-neutral-300">New Balance</span><span className={newBalance < 0 ? "text-[#FF6069]" : "text-white"} data-testid="new-balance-preview">{newBalance}</span></div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setCreditOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={submitCredits} disabled={busy} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="confirm-adjust">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Adjustment"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email installer modal */}
      <Dialog open={emailOpen} onOpenChange={(o) => { if (sending) return; setEmailOpen(o); if (!o) setAttachments([]); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-white/[0.08] bg-[#141414] text-white sm:max-w-xl" data-testid="email-modal">
          <DialogHeader><DialogTitle>Email {inst.business_name}</DialogTitle><DialogDescription className="text-neutral-500">Send a direct email to this installer.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-neutral-300">Template</Label>
              <Select value={email.template} onValueChange={applyTemplate}>
                <SelectTrigger className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="email-template"><SelectValue /></SelectTrigger>
                <SelectContent className="border-white/[0.08] bg-[#141414] text-white">
                  <SelectItem value="general">Blank (free-text)</SelectItem>
                  <SelectItem value="welcome">Welcome</SelectItem>
                  <SelectItem value="low_credits">Low Credits Reminder</SelectItem>
                  <SelectItem value="follow_up">Check-in / Follow-up</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Subject</Label>
              <Input value={email.subject} onChange={(e) => setEmail((s) => ({ ...s, subject: e.target.value }))} className="h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="email-subject" />
            </div>
            <div className="space-y-2">
              <Label className="text-neutral-300">Message</Label>
              <Textarea value={email.body} onChange={(e) => setEmail((s) => ({ ...s, body: e.target.value }))} rows={7} className="rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white" data-testid="email-body" />
            </div>
            <EmailAttachments files={attachments} onChange={setAttachments} disabled={sending} />
          </div>
          <DialogFooter>
            <button onClick={() => { setEmailOpen(false); setAttachments([]); }} disabled={sending} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05] disabled:opacity-50" data-testid="cancel-email-btn">Cancel</button>
            <button onClick={sendEmail} disabled={sending} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="send-email-btn">{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} {sending ? (attachments.length ? "Sending with attachments..." : "Sending...") : "Send Email"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-600">{label}</p>
      <p className="mt-1 text-neutral-200">{value}</p>
    </div>
  );
}
